import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";
import { 
  getUserData, 
  // Comment out unused imports
  /* setUserPremium,
  type PaymentTransaction */
} from "@/lib/firebase";
import { Question, Subject } from '@/types/questions';
//import { getQuestionsBySubjectAndYear } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import StudyMode from '@/components/StudyMode';
import ExamMode from '@/components/ExamMode';
import { Header } from '@/components/Header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getUserData as getUserDataFromFirebase } from '@/lib/firebase/user';
import { toast } from '@/components/ui/use-toast';
import { useUserSync } from '@/hooks/useUserSync';
import { SEO } from '@/components/SEO';
import type { UserData as UserDataFromUser } from '@/lib/firebase/user';
import { testFirebaseConnection, db } from '@/lib/firebase/firebaseClient';
import { collection, doc, getDoc } from 'firebase/firestore';
//import { Input } from '@/components/ui/input';
import PaywallModal from '@/components/PaywallModal';
import { updateUserQuestionAttempts } from '@/lib/firebase/user';
import { setUserPremium } from "@/lib/firebase/user";

interface ErrorDetails {
  name?: string;
  message?: string;
  stack?: string;
}

// Update the helper function
const getModeIndicatorStyles = (mode: 'study' | 'exam') => {
  return mode === 'exam' 
    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : 'bg-green-100 text-green-800 border-green-200';
};

const Quiz: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFirstLoad = useRef(true);
  const { 
    userData, 
    loading: userDataLoading, 
    // Comment out unused destructured values
    // refreshUserData, 
    setUserData 
  } = useUserSync();
  
  // Add null check for location.state
  if (!location.state) {
    navigate('/subjects');
    return null;
  }

  // Now we can safely destructure since we've checked for null
  const { subject, mode = 'study', year: initialYear = '2015' } = location.state as { 
    subject: Subject; 
    mode?: 'study' | 'exam';
    year?: string;
  };

  // Use mode with default value
  const quizMode: 'study' | 'exam' = mode === 'exam' ? 'exam' : 'study';
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [timeLeft, setTimeLeft] = useState(60 * 60); // 1 hour in seconds
  const [isActive, setIsActive] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [yearQuestionCount, setYearQuestionCount] = useState<number>(0);
  const [showStopConfirmation, setShowStopConfirmation] = useState(false);
  const [_selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  //const [imageUrl, setImageUrl] = useState('');
  //const [editImageUrl, setEditImageUrl] = useState('');

  // Add new state for tracking year progress
  const [_yearScores, setYearScores] = useState<Record<string, number>>({});

  // Add this state near other state declarations
  const [_hasStartedYear, setHasStartedYear] = useState(false);

  // Add new state to track if user has hit limit
  const [hasHitFreeLimit, setHasHitFreeLimit] = useState(false);

  const years = ['2015', '2016', '2017', '2018'];

  const [showPaywall, setShowPaywall] = useState(false);
  const [hasHitPaywall, setHasHitPaywall] = useState(false);

  const handleYearChange = async (year: string) => {
    // Check if user is trying to access restricted years
    const isRestrictedYear = year !== '2015';
    const isFreePlan = !userData?.isPremium;

    if (isRestrictedYear && isFreePlan) {
      setShowPaywall(true);
      return;
    }

    // If they're allowed to change year
    setSelectedYear(year);
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setScore(0);
    setSelectedAnswer(null);
    setHasStartedYear(false);
    await fetchQuestions();
  };

  const handleStopQuiz = () => {
    setShowStopConfirmation(true);
  };

  const handleConfirmStop = () => {
    setShowStopConfirmation(false);
    setHasHitFreeLimit(false); // Reset the limit flag
    navigate('/subjects');
  };

  const handleExamSubmit = (answers: (string | null)[]) => {
    let correctCount = 0;
    questions.forEach((question, index) => {
      const selectedAnswer = answers[index];
      // Compare directly with the correct answer
      if (selectedAnswer === question.answer) {
        correctCount++;
      }
    });
    
    const percentageScore = (correctCount / questions.length) * 100;
    setScore(correctCount);
    setYearScores(prev => ({
      ...prev,
      [selectedYear]: percentageScore
    }));
    setShowResults(true);
    setHasStartedYear(false); // Reset started state after completion
  };

  // Update handleStudyModeComplete to reset hasStartedYear
  const handleStudyModeComplete = (correctAnswers: number) => {
    const percentageScore = (correctAnswers / questions.length) * 100;
    setScore(correctAnswers);
    setYearScores(prev => ({
      ...prev,
      [selectedYear]: percentageScore
    }));
    setShowResults(true);
    setHasStartedYear(false); // Reset when year is completed
  };

  // Add this effect at the top of the Quiz component
  useEffect(() => {
    const testConnection = async () => {
      const isConnected = await testFirebaseConnection();
      console.log('Firebase connection status:', isConnected);
    };
    testConnection();
  }, []);

  // Modify the fetchQuestions function
  const fetchQuestions = async () => {
    if (!subject || !selectedYear) return;

    try {
      setLoading(true);
      const subjectLower = subject.toLowerCase();
      
      console.log('Fetching questions for:', {
        subject: subjectLower,
        year: selectedYear
      });
      
      const questionsRef = collection(db, subjectLower);
      const yearDocRef = doc(questionsRef, selectedYear);
      const yearDocSnap = await getDoc(yearDocRef);
      
      if (yearDocSnap.exists()) {
        const data = yearDocSnap.data();
        console.log('Raw Firestore data:', data);
        
        let questionsArray = [];
        
        if (data && data.questions) {
          if (Array.isArray(data.questions)) {
            questionsArray = data.questions;
          } else if (typeof data.questions === 'object') {
            questionsArray = Object.values(data.questions);
          }
        }

        if (questionsArray.length > 0) {
          // Log the raw first question to see all available fields
          console.log('Raw first question:', questionsArray[0]);

          const formattedQuestions = questionsArray.map((q: any, index: number) => {
            // Clean the answer string by removing special quotes and trimming
            const cleanAnswer = (str: string) => {
              if (!str) return '';
              return str.replace(/[""]/g, '').trim();
            };

            let correctAnswer = '';
            let correctOption = ''; // Add this to store the letter answer
            
            if (subjectLower === 'english' && selectedYear === '2015') {
              // Get raw answer first
              const rawAnswer = q.answer || q.correctAnswer || q.correct_answer || '';
              const cleaned = cleanAnswer(rawAnswer);
              
              // If it's a single letter answer (A, B, C, D)
              if (cleaned.length === 1 && /[A-D]/i.test(cleaned)) {
                correctOption = cleaned.toUpperCase();
                const optionIndex = correctOption.charCodeAt(0) - 65;
                if (q.options && q.options[optionIndex]) {
                  correctAnswer = q.options[optionIndex];
                }
              } else {
                correctAnswer = cleaned;
              }
            } else {
              // For other years/subjects, use the existing logic but clean the answer
              const rawAnswer = q.correct_answer || q.correctAnswer || q.correctOption || q.answer || '';
              correctAnswer = cleanAnswer(rawAnswer);
            }

            return {
              id: String(index + 1),
              text: q.question || q.text || '',
              question: q.question || q.text || '',
              options: Array.isArray(q.options) ? q.options : [],
              answer: correctAnswer,
              correctOption: correctOption || correctAnswer, // Add this field to help with highlighting
              imageUrl: q.imageUrl || '',
              subject: subjectLower,
              year: selectedYear
            };
          });
          
          // Log the first formatted question
          console.log('First formatted question:', formattedQuestions[0]);
          
          setQuestions(formattedQuestions);
          setYearQuestionCount(formattedQuestions.length);
        } else {
          console.log('No questions found in document');
          setQuestions([]);
          setYearQuestionCount(0);
        }
      } else {
        console.log('Year document does not exist');
        setQuestions([]);
        setYearQuestionCount(0);
      }
    } catch (err) {
      const error = err as ErrorDetails;
      console.error('Error fetching questions:', {
        name: error?.name || 'Unknown Error',
        message: error.message,
        stack: error.stack
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load questions. Please try again.",
      });
      setQuestions([]);
      setYearQuestionCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Modify the fetchQuestions useEffect
  useEffect(() => {
    fetchQuestions();
  }, [subject, selectedYear]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            setIsActive(false);
            navigate('/');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isActive, timeLeft, navigate]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.email) { // Add null check
        const userData = await getUserDataFromFirebase(user.email);
        console.log('Fetched user data:', userData);
        setUserData(userData as UserDataFromUser);
      }
    };
    fetchUserData();
  }, [user]);

  // Centralize the attempt tracking logic
  const handleAnswerSubmit = async (_selectedOption: string) => {
    if (!user?.email || !userData) return;

    // Only track attempts for free users on 2015 questions
    if (!userData.isPremium && selectedYear === '2015') {
      const currentAttempts = userData.questionAttempts || 0;
      const newAttempts = currentAttempts + 1;
      
      try {
        // Update Firebase first
        await updateUserQuestionAttempts(user.email, newAttempts);
        
        // Then update local state
        setUserData(prev => ({
          ...prev!,
          questionAttempts: newAttempts
        }));

        // Check if user has hit limit
        if (newAttempts >= 5) {
          setHasHitFreeLimit(true);
          setHasHitPaywall(true);
          setShowPaywall(true);
        }
      } catch (error) {
        console.error('Failed to update attempts:', error);
      }
    }
  };

  // Add this effect to monitor premium status
  useEffect(() => {
    if (userData?.isPremium) {
      console.log('Premium status is active, user should have full access');
    }
  }, [userData?.isPremium]);

  // Add this effect to monitor state changes
  useEffect(() => {
    console.log('State updated:', {
      isPremium: userData?.isPremium,
      questionAttempts: userData?.questionAttempts,
      currentYear: selectedYear,
      questionsCount: questions.length
    });
  }, [userData, selectedYear, questions]);

  // Add this effect to monitor userData changes
  useEffect(() => {
    console.log('userData changed:', {
      isPremium: userData?.isPremium,
      attempts: userData?.questionAttempts,
      timestamp: new Date().toISOString()
    });
  }, [userData]);

  // Add this effect to refetch user data periodically after payment
  useEffect(() => {
    if (user?.email && userData?.isPremium) {
      const interval = setInterval(async () => {
        // Add non-null assertion since we already checked email exists
        const freshData = await getUserData(user.email!);
        if (freshData?.isPremium !== userData.isPremium) {
          setUserData(freshData as UserDataFromUser);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [user?.email, userData?.isPremium]);

  // Update the initialization effect to be more careful with user data
  useEffect(() => {
    // Only set default values if:
    // 1. We're not loading
    // 2. User data is null
    // 3. This is the first load
    if (!userDataLoading && userData === null && isFirstLoad.current) {
      // Set initial default values only on first load
      setUserData({
        isPremium: false,
        questionAttempts: 0,
        // ... other default values as needed
      } as UserDataFromUser);
      isFirstLoad.current = false;
    }
  }, [userDataLoading, userData, setUserData]);

  // Update AttemptsDisplay to be more reliable
  const AttemptsDisplay = () => {
    if (userDataLoading || !userData) return null;

    const attempts = userData.questionAttempts || 0;
    const displayAttempts = Math.min(attempts, 5);
    const progressWidth = Math.min((attempts * 20), 100);

    return (
      <>
        {!userData.isPremium && selectedYear === '2015' && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">
                Free Questions Used
              </span>
              <span className="text-sm font-bold text-blue-800">
                {displayAttempts} / 5
              </span>
            </div>
            <div className="mt-2 h-1.5 bg-blue-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progressWidth}%` }}
              />
            </div>
          </div>
        )}
      </>
    );
  };

  // Add this effect after your other useEffects
  useEffect(() => {
    console.log('Questions state updated:', {
      count: questions.length,
      subject,
      year: selectedYear,
      firstQuestion: questions[0]
    });
  }, [questions, subject, selectedYear]);

  // Add these handlers
  const handleShowPaywall = () => {
    setShowPaywall(true);
    setHasHitPaywall(true);
  };

  // Update handleClosePaywall to ensure attempts stay at 5
  const handleClosePaywall = () => {
    setShowPaywall(false);
    // Return to 2015 if they're not premium
    if (!userData?.isPremium) {
      setSelectedYear('2015');
      // Don't update attempts if they've hit the limit
      if (!hasHitFreeLimit && userData?.questionAttempts && userData.questionAttempts >= 5) {
        setUserData(prev => ({
          ...prev!,
          questionAttempts: 5
        }));
      }
      fetchQuestions();
    }
  };

  const handlePaymentSuccess = async (paymentDetails: any) => {
    try {
      if (!user?.email) {
        throw new Error('User email not found');
      }

      // Update the user's premium status in Firebase
      await setUserPremium(user.email, paymentDetails);
      
      // Update local state
      setUserData(prev => ({
        ...prev!,
        isPremium: true,
        questionAttempts: 0 // Reset attempts since they're now premium
      }));

      // Close the paywall
      setShowPaywall(false);
      setHasHitPaywall(false);
      setHasHitFreeLimit(false);

      // Show success message
      toast({
        title: "Premium Access Activated!",
        description: "You now have unlimited access to all questions and years.",
        variant: "default",
      });

      // Refresh the questions if they were trying to access a premium year
      if (selectedYear !== '2015') {
        await fetchQuestions();
      }

    } catch (error) {
      console.error('Failed to process payment:', error);
      toast({
        title: "Error",
        description: "Failed to update premium status. Please contact support.",
        variant: "destructive",
      });
    }
  };

  // For the edit dialog
  //const handleEditImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //  setEditImageUrl(e.target.value);
  //};

  // Show loading state only when actually loading user data
  if (userDataLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center mt-16">
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center mt-16">
          <div className="text-gray-600">Loading questions...</div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const totalQuestions = questions.length;
    const percentageScore = (score / totalQuestions) * 100;
    const hasPassed = percentageScore >= 50;
    const canProgressToNextYear = hasPassed && selectedYear !== '2018';
    const nextYear = (parseInt(selectedYear) + 1).toString();

    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-4 mt-16">
          <div className="container max-w-2xl">
            <div className="bg-white rounded-3xl p-8 border border-gray-200 text-center">
              <h2 className="text-3xl font-bold mb-6">Quiz Results</h2>
              
              <div className="mb-8">
                <p className="text-6xl font-bold text-purple-600 mb-2">
                  {score}/{totalQuestions}
                </p>
                <p className="text-xl text-gray-600">
                  {percentageScore.toFixed(1)}% Correct
                </p>
              </div>

              <div className="space-y-4">
                {!hasPassed && (
                  <Button 
                    onClick={() => {
                      setShowResults(false);
                      setCurrentQuestionIndex(0);
                      setScore(0);
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    Retake {selectedYear} Questions
                  </Button>
                )}

                <Button 
                  onClick={() => navigate('/subjects', { state: { mode } })}
                  className="w-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Change Subject
                </Button>

                {canProgressToNextYear && (
                  <Button 
                    onClick={() => handleYearChange(nextYear)}
                    className="w-full bg-[#7C3AED] hover:bg-[#7C3AED]/90"
                  >
                    Start {nextYear} Questions
                  </Button>
                )}
              </div>

              {hasPassed ? (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-700">
                    Congratulations! You've passed {selectedYear} questions.
                    {canProgressToNextYear && " You can now move on to the next year."}
                  </p>
                </div>
              ) : (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-yellow-700">
                    You need to score at least 50% to progress. Keep practicing!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${subject} ${quizMode} - UTME Quiz`}
        description={`Practice ${subject} JAMB UTME questions from ${selectedYear}. ${
          quizMode === 'exam' 
            ? 'Test yourself in exam conditions.' 
            : 'Study with immediate feedback.'
        }`}
        keywords={`JAMB ${subject}, UTME ${selectedYear}, ${subject} practice questions, ${quizMode} mode`}
      />
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 mt-4 sm:mt-8">
          <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-4 max-w-4xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
              {/* Subject, Mode, and Years Container */}
              <div className="w-full flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-2xl sm:text-4xl font-bold capitalize">{subject}</h1>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getModeIndicatorStyles(quizMode)}`}>
                      {quizMode === 'exam' ? 'Exam Mode' : 'Study Mode'}
                    </span>
                  </div>
                  <div className="inline-flex rounded-lg border border-gray-100 p-1">
                    {years.map((year) => (
                      <button
                        key={year}
                        onClick={() => handleYearChange(year)}
                        className={`px-2 sm:px-4 py-1 rounded-md text-sm sm:text-base transition-colors ${
                          selectedYear === year 
                            ? 'bg-[#7C3AED] text-white' 
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timer and Stop Quiz - Mobile */}
                <div className="sm:hidden flex flex-col items-end">
                  <div className="text-gray-500 text-sm text-right mb-1">
                    <span className="font-medium">{formatTime(timeLeft)}</span>
                    <div className="text-xs">Time left</div>
                  </div>
                  <Button 
                    variant="destructive"
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-sm"
                    onClick={handleStopQuiz}
                  >
                    Stop Quiz
                  </Button>
                </div>
              </div>

              {/* Timer and Stop Quiz - Desktop */}
              <div className="hidden sm:flex items-center gap-4">
                <div className="text-gray-500">
                  <span className="font-medium">{formatTime(timeLeft)}</span>
                  <div className="text-sm">Time left</div>
                </div>
                <Button 
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600 text-sm sm:text-base px-4"
                  onClick={handleStopQuiz}
                >
                  Stop Quiz
                </Button>
              </div>
            </div>

            <AttemptsDisplay />

            {questions.length > 0 ? (
              <div className="w-full max-w-4xl mx-auto">
                <div className="bg-white rounded-xl sm:rounded-3xl overflow-hidden">
                  {quizMode === 'study' ? (
                    <StudyMode
                      questions={questions}
                      currentQuestionIndex={currentQuestionIndex}
                      onNextQuestion={() => {
                        setCurrentQuestionIndex(prev => prev + 1);
                        setSelectedAnswer(null);
                      }}
                      onPreviousQuestion={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                      totalQuestions={yearQuestionCount}
                      onAnswerSubmit={handleAnswerSubmit}
                      userData={userData}
                      onShowPaywall={handleShowPaywall}
                      hasHitPaywall={hasHitPaywall}
                      onComplete={handleStudyModeComplete}
                    />
                  ) : (
                    <ExamMode
                      questions={questions}
                      currentQuestionIndex={currentQuestionIndex}
                      onSubmitExam={handleExamSubmit}
                      onPreviousQuestion={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                      onNextQuestion={() => {
                        setCurrentQuestionIndex(prev => prev + 1);
                        setSelectedAnswer(null);
                      }}
                      totalQuestions={yearQuestionCount}
                      onAnswerSubmit={handleAnswerSubmit}
                      userData={userData}
                      onShowPaywall={handleShowPaywall}
                      hasHitPaywall={hasHitPaywall}
                      onComplete={() => {
                        console.log('Exam completed');
                      }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-4 sm:p-8 border border-gray-200 rounded-2xl sm:rounded-3xl mt-4">
                <p className="text-gray-500">No questions available for {subject} {selectedYear}</p>
                <Button 
                  onClick={() => navigate('/')} 
                  className="mt-4"
                >
                  Back to Subject Selection
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={showStopConfirmation} onOpenChange={setShowStopConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to stop?</AlertDialogTitle>
            <AlertDialogDescription>
              Your progress will be lost and you'll need to start over.
              {quizMode === 'exam' && " All your answers will be discarded."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmStop}
              className="bg-red-500 hover:bg-red-600"
            >
              Yes, stop quiz
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add PaywallModal */}
      <PaywallModal 
        isOpen={showPaywall}
        onClose={handleClosePaywall}
        onPaymentSuccess={handlePaymentSuccess}
        userEmail={user?.email || ''}
      />
    </>
  );
};

export default Quiz;